import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Container, Box, makeStyles } from '@material-ui/core';
import EmptyResultSetMessage from 'components/@common/EmptyResultSetMessage';
import { ModalFooter, ModalHeader, ModalContainer } from 'components/@common/Modal';
import ModalCloseButton from 'components/@common/SlideModal/ModalCloseButton';
import ClassroomProductLicenseCard from 'components/@common/ProductLicenses/ClassroomProductLicenseCard';
import {
  ProductCardValidationProvider,
  useProductCardValidation
} from 'components/@common/ProductLicenses/product-card-validation-provider';
import UnsavedChangesWarning from 'components/@common/ProductLicenses/UnsavedChangesWarning';
import PageContainer from 'components/@layout/PageContainer';
import Config from 'config';
import BULK_GROUP_ENROLLMENT_ACTION from 'graph/classroom/bulk-group-enrollment-mutation';
import GET_CLASSROOM_LICENSES_STUDENTS from 'graph/composite/get-classroom-licenses-students-query';
import GET_CLASSROOM_QUERY from 'graph/classroom/get-classroom-query';
import BULK_ASSIGN_AVAILABLE_LICENSES from 'graph/license/bulk-assign-available-licenses-mutation';
import useParseActionRouteParams from 'hooks/route-action/use-parse-action-route-params';
import { flow, filter, sortBy, map, uniq } from 'lodash/fp';
import useStore from 'store';
import { AlertVariant } from 'types/enums';
import {
  OrganizationLicenseUsage,
  ProductType,
  StudentLicenseUsage,
  Student,
  LicenseAction,
  BulkLicenseActionInput
} from 'types/schema-generated-types';
import useDefaultGroupSettings from '../../../hooks/use-default-group-settings';
import licenseUpdateReducer, { ActionType } from './reducer';

interface ManageClassroomProductsModalProps {
  onClose: () => void;
}

const { privilegesGroups } = Config;

const ManageClassroomProductsModal = ({ onClose }: ManageClassroomProductsModalProps) => {
  const classes = useStyles();
  const { t } = useTranslation('ManageClassroomProducts');
  const { classroomId } = useParseActionRouteParams();
  const [{ userPrivilegesCache }, { openAlert }] = useStore();
  const { getGroupEnrollments, getGroupUnenrollments } = useDefaultGroupSettings();
  const [{ invalidProductFamilyIds }] = useProductCardValidation();
  const [highlightProductFamilyId, setHighlightProductFamilyId] = useState<number | null>(null);
  const [licenseUpdates, dispatch] = useReducer(licenseUpdateReducer, []);

  const [loadLicenses, { data, loading: licensesLoading, error: licensesError }] = useLazyQuery(
    GET_CLASSROOM_LICENSES_STUDENTS
  );

  const [
    bulkAssignAvailableLicenses,
    { called: assignCalled, loading: assignLoading }
  ] = useMutation(BULK_ASSIGN_AVAILABLE_LICENSES);

  const [
    bulkGroupEnrollmentAction,
    { called: groupEnrollmentCalled, loading: groupEnrollmentLoading }
  ] = useMutation(BULK_GROUP_ENROLLMENT_ACTION);

  useEffect(() => {
    if (classroomId) loadLicenses({ variables: { classroomId: +classroomId } });
  }, [classroomId, loadLicenses]);

  const hasLicenseAssignPrivileges = userPrivilegesCache.isPrivileged(
    [...privilegesGroups.assignClassroomLicense, ...privilegesGroups.assignOrgLicense],
    {
      classroomId: data?.classroom?.id,
      organizationId: data?.classroom?.organizationId
    }
  );

  const organizationLicenseUsage: OrganizationLicenseUsage[] =
    data?.classroom?.organization?.rootOrganization?.licenseUsage ?? [];

  const assignedProductFamilyStudentCurriculumMap = useMemo(() => {
    // Assigned Product Curriculums by Product Family ID and Student ID
    const result: Map<number, Map<number, number>> = new Map<number, Map<number, number>>();
    const students = data?.classroom?.students ?? [];

    students.forEach((student: Student) => {
      const licenseUsage: StudentLicenseUsage[] = student.licenseUsage ?? [];

      licenseUsage
        .filter(x => x.assignedCount > 0 && !!x.productCurriculum)
        .forEach(x => {
          const productFamilyMap = result.get(x.product.family.id) ?? new Map<number, number>();
          if (!!x.productCurriculum?.id) {
            productFamilyMap.set(student.id, x.productCurriculum.id);
          }

          result.set(x.product.family.id, productFamilyMap);
        });
    });

    return result;
  }, [data]);

  const productFamilyIds: number[] = useMemo(
    () =>
      flow(
        filter((lu: OrganizationLicenseUsage) => lu.product.type === ProductType.STUDENT),
        sortBy('product.family.name'),
        map('product.family.id'),
        uniq
      )(organizationLicenseUsage),
    [organizationLicenseUsage]
  );

  const handleLicenseAssign = (
    productFamilyId: number,
    productCurriculumId: number,
    studentId: number
  ) =>
    dispatch({
      type: ActionType.ADD_UPDATE,
      payload: {
        action: LicenseAction.ASSIGN,
        productFamilyId,
        productCurriculumId,
        studentId
      }
    });

  const handleLicenseUnassign = (
    productFamilyId: number,
    productCurriculumId: number,
    studentId: number
  ) => {
    dispatch({
      type: ActionType.ADD_UPDATE,
      payload: {
        action: LicenseAction.UNASSIGN,
        productFamilyId,
        productCurriculumId,
        studentId
      }
    });
  };

  const handleSubmit = useCallback(async () => {
    if (invalidProductFamilyIds.length > 0) {
      const productFamilyId = productFamilyIds.find(id => invalidProductFamilyIds.includes(id));
      if (productFamilyId) {
        setHighlightProductFamilyId(productFamilyId);
        setTimeout(() => setHighlightProductFamilyId(null), 3000);
      }

      return;
    }

    const assignRequests: BulkLicenseActionInput[] = licenseUpdates.map(u => ({
      studentId: u.studentId,
      action: u.action,
      productCurriculum: { id: u.productCurriculumId },
      replaceExistingProductFamilyLicense: true,
      skipAlreadyLicensed: true
    }));

    await bulkAssignAvailableLicenses({
      variables: { where: { organizationId: data?.classroom?.organization?.id }, assignRequests },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GET_CLASSROOM_LICENSES_STUDENTS,
          variables: { classroomId: +classroomId }
        },
        {
          query: GET_CLASSROOM_QUERY,
          variables: { id: +classroomId }
        }
      ]
    });

    const groupEnrollmentUpdates = [
      ...getGroupEnrollments(+classroomId, data?.classroom?.students, assignRequests),
      ...getGroupUnenrollments(+classroomId, data?.classroom?.students, assignRequests)
    ];

    if (groupEnrollmentUpdates.length > 0) {
      await bulkGroupEnrollmentAction({
        variables: { data: groupEnrollmentUpdates },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: GET_CLASSROOM_LICENSES_STUDENTS,
            variables: { classroomId: +classroomId }
          }
        ]
      });
    }

    openAlert({
      variant: AlertVariant.SUCCESS,
      title: t('Student Licenses Updated'),
      text: t(`The student licenses have been successfully updated.`)
    });
  }, [
    data,
    bulkAssignAvailableLicenses,
    bulkGroupEnrollmentAction,
    classroomId,
    invalidProductFamilyIds,
    getGroupEnrollments,
    getGroupUnenrollments,
    licenseUpdates,
    openAlert,
    productFamilyIds,
    t
  ]);

  useEffect(() => {
    const licenseAssignmentComplete = assignCalled && !assignLoading;
    const groupEnrollmentComplete =
      !groupEnrollmentCalled || (groupEnrollmentCalled && !groupEnrollmentLoading);
    if (licenseAssignmentComplete && groupEnrollmentComplete) onClose();
  }, [assignCalled, assignLoading, groupEnrollmentCalled, groupEnrollmentLoading, onClose]);

  const handleCancelClick = () => {
    if (licenseUpdates.length > 0) {
      openAlert({
        cancelButtonText: t('Exit without saving'),
        confirmButtonText: t('Go back'),
        onCancel: () => onClose(),
        onSubmit: () => false,
        reverseButtons: false,
        text: t('You have unsaved changes. Do you want to save them?'),
        variant: AlertVariant.WARNING
      });

      return;
    }

    onClose();
  };

  return (
    <PageContainer loading={licensesLoading} error={licensesError}>
      <ModalContainer
        className={classes.body}
        Header={
          <>
            <ModalCloseButton onClick={handleCancelClick} tabIndex={0} />
            <ModalHeader
              title={t('manageClassroomProducts', {
                classroomName: data?.classroom?.name
              })}
            />
          </>
        }
        Footer={
          <ModalFooter
            onCancel={handleCancelClick}
            onSubmit={productFamilyIds.length > 0 ? handleSubmit : undefined}
            submitDisabled={assignLoading}
          >
            <Box marginLeft={4}>{licenseUpdates.length > 0 && <UnsavedChangesWarning />}</Box>
          </ModalFooter>
        }
      >
        {productFamilyIds.length === 0 && (
          <EmptyResultSetMessage
            style={{ marginTop: '12px' }}
            message={t('No available licenses', { ns: 'EmptyResultSetMessage' })}
            subtitle={t(`There are no licenses available to assign to students.`, {
              ns: 'EmptyResultSetMessage'
            })}
          />
        )}
        {productFamilyIds.map(productFamilyId => {
          const assignedStudentCurriculumMap = assignedProductFamilyStudentCurriculumMap.get(
            productFamilyId
          );

          return (
            <Container key={productFamilyId} maxWidth="lg" className={classes.cardContainer}>
              <ClassroomProductLicenseCard
                productFamilyId={productFamilyId}
                assignable={hasLicenseAssignPrivileges}
                students={data?.classroom.students}
                assignedProductCurriculumMap={assignedStudentCurriculumMap}
                pendingUpdates={licenseUpdates.filter(x => x.productFamilyId === productFamilyId)}
                organizationLicenseUsage={
                  organizationLicenseUsage.filter(
                    x => x.product.family.id === productFamilyId
                  ) as OrganizationLicenseUsage[]
                }
                error={highlightProductFamilyId === productFamilyId}
                onLicenseAssign={handleLicenseAssign}
                onLicenseUnassign={handleLicenseUnassign}
              />
            </Container>
          );
        })}
      </ModalContainer>
    </PageContainer>
  );
};

const ManageClassroomProductsModalWrapper = (props: ManageClassroomProductsModalProps) => (
  <ProductCardValidationProvider>
    <ManageClassroomProductsModal {...props} />;
  </ProductCardValidationProvider>
);

const useStyles = makeStyles(theme => ({
  body: {
    background: '#EEEEEE'
  },
  cardContainer: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2)
  },
  nextButton: {
    marginRight: theme.spacing(6)
  },
  cancelButton: {
    marginRight: theme.spacing(3)
  },
  buttonLabels: {
    fontSize: '1rem',
    textTransform: 'initial'
  },
  saveChangesMessage: {
    marginLeft: theme.spacing(6)
  }
}));

export default ManageClassroomProductsModalWrapper;
